import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIntegrationHealth } from '@/hooks/useIntegrationHealth';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock,
  Bell,
  RefreshCw,
  Eye,
  Check
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { IntegrationHealth, SystemAlert } from '@/lib/security/types';

function StatusBadge({ status }: { status: IntegrationHealth['status'] }) {
  const config = {
    healthy: { icon: CheckCircle, color: 'bg-primary/10 text-primary border-primary/30' },
    degraded: { icon: AlertTriangle, color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
    down: { icon: XCircle, color: 'bg-red-500/10 text-red-500 border-red-500/30' },
    unknown: { icon: Clock, color: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  };

  const { icon: Icon, color } = config[status];

  return (
    <Badge variant="outline" className={color}>
      <Icon className="w-3 h-3 mr-1" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function SeverityBadge({ severity }: { severity: SystemAlert['severity'] }) {
  const config = {
    critical: 'bg-red-500/10 text-red-500 border-red-500/30',
    warning: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    info: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  };

  return (
    <Badge variant="outline" className={config[severity]}>
      {severity.toUpperCase()}
    </Badge>
  );
}

function IntegrationCard({ health }: { health: IntegrationHealth }) {
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">{health.integration_name}</h3>
          <StatusBadge status={health.status} />
        </div>
        <p className="text-xs text-muted-foreground mb-3">{health.integration_type}</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Success Rate</p>
            <p className="font-medium">
              {health.success_count + health.failure_count > 0
                ? Math.round((health.success_count / (health.success_count + health.failure_count)) * 100)
                : 0}%
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Avg Response</p>
            <p className="font-medium">
              {health.avg_response_ms ? `${health.avg_response_ms}ms` : '—'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Last Check</p>
            <p className="font-medium text-xs">
              {health.last_check_at 
                ? formatDistanceToNow(new Date(health.last_check_at), { addSuffix: true })
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Failures</p>
            <p className="font-medium">{health.failure_count}</p>
          </div>
        </div>
        {health.error_message && (
          <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-500">
            {health.error_message}
          </div>
        )}
      </div>
    </Card>
  );
}

function AlertCard({ 
  alert, 
  onAcknowledge, 
  onResolve 
}: { 
  alert: SystemAlert;
  onAcknowledge: () => void;
  onResolve: () => void;
}) {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <SeverityBadge severity={alert.severity} />
            <Badge variant="outline" className="text-xs">
              {alert.status}
            </Badge>
          </div>
          <h4 className="font-medium">{alert.title}</h4>
          {alert.description && (
            <p className="text-sm text-muted-foreground">{alert.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          {alert.status === 'open' && (
            <Button variant="outline" size="sm" onClick={onAcknowledge}>
              <Eye className="w-3 h-3 mr-1" />
              Acknowledge
            </Button>
          )}
          {alert.status !== 'resolved' && (
            <Button variant="outline" size="sm" onClick={onResolve}>
              <Check className="w-3 h-3 mr-1" />
              Resolve
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span>Type: {alert.alert_type}</span>
        <span>Triggered: {format(new Date(alert.triggered_at), 'PPp')}</span>
        {alert.resource_type && (
          <span>Resource: {alert.resource_type}</span>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsHealthPage() {
  const { clientId, isSuperAdmin } = usePermissions();
  const { 
    healthStatuses, 
    alerts, 
    healthSummary, 
    alertSummary,
    isLoading,
    acknowledgeAlert,
    resolveAlert,
  } = useIntegrationHealth(isSuperAdmin() ? undefined : clientId || undefined);

  return (
    <OpsLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-2">Integration Health</h1>
        <p className="text-muted-foreground mb-6">Monitor system integrations and alerts</p>
        
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{healthSummary.healthy}</p>
                    <p className="text-xs text-muted-foreground">Healthy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{healthSummary.degraded}</p>
                    <p className="text-xs text-muted-foreground">Degraded</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <XCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{healthSummary.down}</p>
                    <p className="text-xs text-muted-foreground">Down</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <Bell className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{alertSummary.critical}</p>
                    <p className="text-xs text-muted-foreground">Critical Alerts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Tabs defaultValue="integrations">
            <TabsList>
              <TabsTrigger value="integrations">
                <Activity className="w-4 h-4 mr-2" />
                Integrations ({healthSummary.total})
              </TabsTrigger>
              <TabsTrigger value="alerts">
                <Bell className="w-4 h-4 mr-2" />
                Alerts ({alertSummary.open})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="integrations" className="mt-4">
              {isLoading ? (
                <div className="text-center py-12 text-muted-foreground">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading integrations...
                </div>
              ) : healthStatuses.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No integrations configured yet.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {healthStatuses.map(health => (
                    <IntegrationCard key={health.id} health={health} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="alerts" className="mt-4">
              {alerts.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-primary mb-4" />
                    <p className="text-muted-foreground">No active alerts!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {alerts.map(alert => (
                    <AlertCard 
                      key={alert.id} 
                      alert={alert}
                      onAcknowledge={() => acknowledgeAlert(alert.id)}
                      onResolve={() => resolveAlert(alert.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </OpsLayout>
  );
}
